import { Table, Tooltip, Empty, Input, Select, message } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  UserSwitchOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { API_URL } from "../../../constants";
import { useSelector } from "react-redux";
import moment from "moment-timezone";
import UsersModal from "./UsersModal";
import PasswordModal from "./PasswordModal";
import DeleteModal from "./DeleteModal";
import "../workingmonth.css";

const Badge = ({ tone = "neutral", children }) => (
  <span className={`wm-badge wm-badge-sm wm-badge-${tone}`}>{children}</span>
);

const paginationProps = {
  defaultPageSize: 20,
  pageSizeOptions: ["10", "20", "50", "100"],
  showSizeChanger: true,
  showQuickJumper: true,
  showTotal: (t, r) => `${r[0]}–${r[1]} din ${t} utilizatori`,
};

const PERMISSIONS = {
  modifications: "Modificări",
  invoice: "Factura",
  raports: "Rapoarte",
};

const UsersTable = ({ isModalVisible, setIsModalVisible, headerExtra }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  // soft delete: implicit se vad doar conturile active
  const [statusFilter, setStatusFilter] = useState("active");
  const [restoring, setRestoring] = useState(null);
  const token = useSelector((state) => state.user.currentUser?.accessToken);

  const getUsers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API_URL + "user/us/list", {
        headers: { Authorization: "Bearer " + token },
      });
      setUsers(res.data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isModalVisible) getUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalVisible]);

  const isActive = (u) => u.active !== false;

  const filteredUsers = useMemo(() => {
    const s = search.trim().toLowerCase();
    return users.filter((u) => {
      if (statusFilter === "active" && !isActive(u)) return false;
      if (statusFilter === "inactive" && isActive(u)) return false;
      if (!s) return true;
      return [u.nume, u.prenume, u.email]
        .map((v) => (v || "").toLowerCase())
        .some((v) => v.includes(s));
    });
  }, [users, search, statusFilter]);

  const activeUsers = users.filter(isActive);
  const inactivi = users.length - activeUsers.length;
  const admini = activeUsers.filter((u) => (u.role || "").toLowerCase() === "admin").length;
  const gestionari = activeUsers.filter(
    (u) => (u.role || "").toLowerCase() === "gestionar"
  ).length;

  const handleRestore = async (record) => {
    setRestoring(record.id);
    try {
      await axios.post(
        API_URL + "user/us/restore",
        { id: record.id },
        { headers: { Authorization: "Bearer " + token } }
      );
      message.success(
        `${[record.nume, record.prenume].filter(Boolean).join(" ") || record.email} a fost reactivat.`
      );
      await getUsers();
    } catch (error) {
      console.log(error);
      message.error(error.response?.data?.message || "Reactivarea a eșuat.");
    } finally {
      setRestoring(null);
    }
  };

  const mainColumns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 60,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: "Utilizator",
      key: "user",
      sorter: (a, b) =>
        `${a.nume || ""} ${a.prenume || ""}`.localeCompare(
          `${b.nume || ""} ${b.prenume || ""}`
        ),
      render: (_, r) => (
        <div className="wm-user-cell" style={isActive(r) ? undefined : { opacity: 0.6 }}>
          <div className="wm-user-name" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {[r.nume, r.prenume].filter(Boolean).join(" ") || "—"}
            {isActive(r) ? null : (
              <Tooltip
                title={
                  r.deactivatedAt
                    ? `Dezactivat la ${moment(r.deactivatedAt).format("DD.MM.YYYY, HH:mm")}`
                    : "Cont dezactivat"
                }
              >
                <span>
                  <Badge tone="bad">inactiv</Badge>
                </span>
              </Tooltip>
            )}
          </div>
          <div className="wm-user-email">{r.email}</div>
        </div>
      ),
    },
    {
      title: "Rol",
      dataIndex: "role",
      key: "role",
      width: 110,
      sorter: (a, b) => (a.role || "").localeCompare(b.role || ""),
      filters: [
        { text: "Admin", value: "admin" },
        { text: "Gestionar", value: "gestionar" },
      ],
      onFilter: (value, record) => (record.role || "").toLowerCase() === value,
      render: (v) =>
        (v || "").toLowerCase() === "admin" ? (
          <Badge tone="accent">Admin</Badge>
        ) : (
          <Badge tone="neutral">Gestionar</Badge>
        ),
    },
    {
      title: "APC",
      key: "userCustomersCount",
      width: 70,
      align: "center",
      sorter: (a, b) => a.UserCustomers.length - b.UserCustomers.length,
      render: (_, record) => record.UserCustomers.length,
    },
    {
      title: "Servicii",
      key: "userServiceCount",
      width: 85,
      align: "center",
      sorter: (a, b) =>
        (a.allservices ? 999 : a.UserServices.length) -
        (b.allservices ? 999 : b.UserServices.length),
      render: (_, record) =>
        record.allservices ? "toate" : record.UserServices.length,
    },
    {
      title: "Permisiuni",
      key: "userPermisions",
      width: 240,
      render: (_, record) => {
        const keys = Object.keys(PERMISSIONS);
        const perms = keys.filter((key) => record[key]);
        if (perms.length === keys.length) {
          return <Badge tone="good">Toate</Badge>;
        }
        if (!perms.length) {
          return <span style={{ color: "#9c9ca1" }}>—</span>;
        }
        return (
          <span style={{ display: "inline-flex", gap: 4, whiteSpace: "nowrap" }}>
            {perms.map((key) => (
              <Badge key={key} tone="good">
                {PERMISSIONS[key]}
              </Badge>
            ))}
          </span>
        );
      },
    },
    {
      title: "",
      key: "edit",
      width: 128,
      align: "right",
      render: (_, record) => (
        <span style={{ display: "inline-flex", gap: 6 }}>
          <Tooltip title="Editează utilizatorul">
            <button
              type="button"
              className="wm-btn wm-btn-secondary wm-btn-sm"
              onClick={() => handleEdit(record)}
            >
              <EditOutlined />
            </button>
          </Tooltip>
          <Tooltip title="Schimbă parola">
            <button
              type="button"
              className="wm-btn wm-btn-secondary wm-btn-sm"
              onClick={() => handleEditPassword(record)}
            >
              <UserSwitchOutlined />
            </button>
          </Tooltip>
          {isActive(record) ? (
            <Tooltip title="Dezactivează utilizatorul (nu va mai putea intra în aplicație)">
              <button
                type="button"
                className="wm-btn wm-btn-danger wm-btn-sm"
                onClick={() => handleDelete(record)}
              >
                <DeleteOutlined />
              </button>
            </Tooltip>
          ) : (
            <Tooltip title="Reactivează utilizatorul">
              <button
                type="button"
                className={`wm-btn wm-btn-success wm-btn-sm${restoring === record.id ? " wm-btn-loading" : ""}`}
                disabled={restoring === record.id}
                onClick={() => handleRestore(record)}
              >
                <UndoOutlined />
              </button>
            </Tooltip>
          )}
        </span>
      ),
    },
  ];

  const handleEdit = (record) => {
    setIsModalVisible({
      type: "edit",
      id: record.id,
      email: record.email,
      nume: record.nume,
      prenume: record.prenume,
      role: record.role.toLowerCase(),
      customers: record.UserCustomers.map((uc) => uc.customerId),
      services: record.UserServices.map((uc) => uc.SERVICEID),
      allservices: record.allservices,
      modifications: record.modifications,
      invoice: record.invoice,
      raports: record.raports,
    });
  };

  const handleEditPassword = (record) => {
    setIsModalVisible({
      type: "password",
      id: record.id,
      nume: record.nume,
      prenume: record.prenume,
    });
  };

  const handleDelete = (record) => {
    setIsModalVisible({
      type: "delete",
      id: record.id,
      nume: record.nume,
      prenume: record.prenume,
    });
  };

  const expandedRowRender = (record) => {
    const expandedColumns = [
      {
        title: "Contract",
        dataIndex: ["Customer", "CONTRACT"],
        key: "CONTRACT",
      },
      {
        title: "Data contract",
        dataIndex: ["Customer", "DT_CONT"],
        key: "DT_CONT",
        render: (text) => (
          <span className="wm-log-time">
            {text ? moment(text).format("DD.MM.YYYY") : "—"}
          </span>
        ),
      },
      {
        title: "Abonat",
        dataIndex: ["Customer", "ABON"],
        key: "ABON",
      },
      {
        title: "Adresă",
        dataIndex: ["Customer", "ADR_AB"],
        key: "ADR_AB",
      },
      {
        title: "KF",
        dataIndex: ["Customer", "KF_AB"],
        key: "KF_AB",
      },
      {
        title: "Telefon",
        dataIndex: ["Customer", "TEL_AB"],
        key: "TEL_AB",
      },
    ];

    return (
      <Table
        columns={expandedColumns}
        dataSource={record.UserCustomers}
        pagination={false}
        size="small"
        rowKey={(r) => r.Customer.CONTRACT}
        locale={{ emptyText: <Empty description={"Fără APC-uri atribuite"} /> }}
      />
    );
  };

  return (
    <>
      {/* ===== SUMAR ===== */}
      <div className="wm-stats" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
        <div className="wm-stat">
          <div className="wm-stat-label">Utilizatori activi</div>
          <div className="wm-stat-value">{activeUsers.length}</div>
          <div className="wm-stat-meta">
            {inactivi ? `+ ${inactivi} ${inactivi === 1 ? "cont inactiv" : "conturi inactive"}` : "niciun cont inactiv"}
          </div>
        </div>
        <div className="wm-stat wm-stat-warn">
          <div className="wm-stat-label">Admini</div>
          <div className="wm-stat-value">{admini}</div>
          <div className="wm-stat-meta">acces complet</div>
        </div>
        <div className="wm-stat wm-stat-good">
          <div className="wm-stat-label">Gestionari</div>
          <div className="wm-stat-value">{gestionari}</div>
          <div className="wm-stat-meta">lucrează în aplicația gestionar</div>
        </div>
      </div>

      {/* ===== TABEL ===== */}
      <div className="wm-card wm-history">
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 12,
            marginBottom: 14,
          }}
        >
          <h3 className="wm-card-title" style={{ margin: 0, flex: "0 0 auto" }}>
            Utilizatori
          </h3>
          <Input.Search
            placeholder="Caută după nume, prenume sau email..."
            allowClear
            style={{ width: 280 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 170 }}
            options={[
              { value: "active", label: `Activi (${activeUsers.length})` },
              { value: "inactive", label: `Inactivi (${inactivi})` },
              { value: "all", label: `Toți (${users.length})` },
            ]}
          />
          <span style={{ marginLeft: "auto" }}>{headerExtra || null}</span>
        </div>
        <Table
          columns={mainColumns}
          dataSource={filteredUsers}
          expandable={{ expandedRowRender }}
          rowKey="id"
          size="small"
          loading={loading}
          pagination={paginationProps}
          scroll={{ x: 860 }}
          locale={{
            emptyText: (
              <Empty
                description={
                  search
                    ? "Niciun utilizator nu se potrivește căutării"
                    : statusFilter === "inactive"
                      ? "Nu există conturi inactive"
                      : "Nu există utilizatori"
                }
              />
            ),
          }}
        />
      </div>

      {isModalVisible?.type === "edit" ? (
        <UsersModal
          isModalVisible={isModalVisible}
          setIsModalVisible={setIsModalVisible}
        />
      ) : null}

      {isModalVisible?.type === "password" ? (
        <PasswordModal
          isModalVisible={isModalVisible}
          setIsModalVisible={setIsModalVisible}
        />
      ) : null}

      {isModalVisible?.type === "delete" ? (
        <DeleteModal
          isModalVisible={isModalVisible}
          setIsModalVisible={setIsModalVisible}
        />
      ) : null}
    </>
  );
};

export default UsersTable;
